import { TeamBase } from '../../shared/models/team-base';
import type { IGameRecords } from '../../shared/interfaces/IGameRecords';
import type { IGame } from '../../shared/interfaces/IGame';
import { RankMethod } from '../../shared/enums/rank-method';
import { NflGameRecords } from './game-records';
import { Enumerable } from '../../shared/linq';

export class NflTeam extends TeamBase {
  /** 1 = 6-team playoff, 2 = 7-team playoff */
  private readonly postSeasonVersion: number;

  constructor(
    teamAbbreviation: string,
    regionName: string,
    teamName: string,
    conference: string,
    division: string,
    teamID: number,
    postSeasonVersion: number,
    teamLogo: string
  ) {
    super(teamAbbreviation, regionName, teamName, conference, division, teamID);
    this.postSeasonVersion = postSeasonVersion;
    this.TeamLogo = teamLogo;
  }

  protected override createGameRecords(): IGameRecords {
    // Called from base constructor — at this point postSeasonVersion is not yet set,
    // but NflGameRecords only needs Wins/Losses/Ties/TeamNameObs/FakeObs.
    return new NflGameRecords(
      this.Wins,
      this.Losses,
      this.Ties,
      this.TeamNameObs,
      this.FakeObs
    );
  }

  protected override createConferenceCss(): string {
    this.FakeObs();
    if (this.postSeasonVersion === 1) {
      if (this.ConferenceRank() <= 6) {
        return 'in-post-season';
      }
    } else if (this.postSeasonVersion === 2) {
      if (this.ConferenceRank() <= 7) {
        return 'in-post-season';
      }
    }
    return 'out-post-season';
  }

  protected override createRankMethodText(method?: RankMethod): string {
    switch (method ?? this.RankMethod()) {
      case RankMethod.AllPCT:
        return 'Pct vs League';
      case RankMethod.ConfPCT:
        return 'Pct vs Conference';
      case RankMethod.DivPCT:
        return 'Pct vs Div';
      case RankMethod.SOS:
        return 'Strength of Schedule';
      case RankMethod.SOV:
        return 'Strength of Victory';
      case RankMethod.DivTop_AllPCT:
        return 'Top of Division - Pct vs League';
      case RankMethod.DivTop_DivPCT:
        return 'Top of Division - Pct vs Div';
      case RankMethod.DivTop_ConfPCT:
        return 'Top of Division - Pct vs Conference';
      case RankMethod.DivTop_SOS:
        return 'Top of Division - Strength of Schedule';
      case RankMethod.DivTop_SOV:
        return 'Top of Division - Strength of Victory';
      case RankMethod.DivTop_HTH:
        return 'Top of Division - Head to Head';
      case RankMethod.HTH:
        return 'Head-to-Head Tiebreaker';
      case RankMethod.CG:
        return 'Common Games';
      case RankMethod.CoinFlip:
        return 'Coin Flip';
      case RankMethod.CommonGames:
        return 'Common Games';
      case RankMethod.CommonPct:
        return 'Common Pct';
      case RankMethod.DivTop_CommonPct:
        return 'Top of Division - Common Pct';
      default:
        return '';
    }
  }

  /**
   * NFL uses gameScheduleNum for deduplication (not gameGuid like other sports).
   */
  override initSubscribers(): void {
    const self = this;
    if (!self.isInit) {
      self.Wins.subscribe((changes) => {
        changes.forEach((change) => {
          if (change.status === 'added') {
            self.Wins(
              Enumerable.From(self.Wins())
                .Distinct((p: IGame) => p.gameScheduleNum)
                .ToArray()
            );
            self.Losses.remove((p: IGame) => p.gameScheduleNum === change.value.gameScheduleNum);
          }
        });
      }, null, 'arrayChange');

      self.Losses.subscribe((changes) => {
        changes.forEach((change) => {
          if (change.status === 'added') {
            self.Losses(
              Enumerable.From(self.Losses())
                .Distinct((p: IGame) => p.gameScheduleNum)
                .ToArray()
            );
            self.Wins.remove((p: IGame) => p.gameScheduleNum === change.value.gameScheduleNum);
          }
        });
      }, null, 'arrayChange');

      self.Ties.subscribe((changes) => {
        changes.forEach((change) => {
          if (change.status === 'added') {
            self.Ties(
              Enumerable.From(self.Ties())
                .Distinct((p: IGame) => p.gameScheduleNum)
                .ToArray()
            );
            self.Wins.remove((p: IGame) => p.gameScheduleNum === change.value.gameScheduleNum);
            self.Losses.remove((p: IGame) => p.gameScheduleNum === change.value.gameScheduleNum);
          }
        });
      }, null, 'arrayChange');

      self.GameRecords.initSubscribers();
      self.isInit = true;
    }
  }

  override reset(): void {
    // NFL reset: SOVObj/SOSObj back to { Wins: 0, Losses: 0, Ties: 0 }
    const emptyWLT = { Wins: 0, Losses: 0, Ties: 0 };
    (this.SOVObj as KnockoutObservable<unknown>)(emptyWLT);
    this.SOVObj.valueHasMutated();
    (this.SOSObj as KnockoutObservable<unknown>)(emptyWLT);
    this.SOSObj.valueHasMutated();
    this.SOV(0);
    this.SOS(0);
    this.Wins([]);
    this.Losses([]);
    this.Ties([]);
    this.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    this.Tiebreakers({
      HTH: [],
      Sweeps: [],
      DivHTH: [],
    });
    this.ConferenceRank(null);
  }
}
